import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Modal,
  Stack,
  Group,
  Button,
  Loader,
  Center,
  Alert,
  Box,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconDownload,
  IconPrinter,
} from "@tabler/icons-react";
import { apiClient } from "../../lib";
import { REPORT_PDF_REQUEST_TIMEOUT_MS } from "../../services/signOff.service";
import {
  getResponseDownloadFilename,
  triggerBlobDownload,
} from "../../utils/downloadFilename";

interface SignedReportViewerModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber?: string;
  lastSigned?: boolean;
}

export function SignedReportViewerModal({
  opened,
  onClose,
  caseId,
  caseNumber,
  lastSigned = false,
}: SignedReportViewerModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const downloadFileNameRef = useRef("report.pdf");
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!opened || !caseId) return undefined;

    let revokedUrl: string | null = null;
    let cancelled = false;

    setLoading(true);
    setError(null);
    setBlobUrl(null);
    blobRef.current = null;

    apiClient
      .get(
        `/cases/${caseId}/report/pdf${lastSigned ? "?lastSigned=true" : ""}`,
        {
          responseType: "blob",
          timeout: REPORT_PDF_REQUEST_TIMEOUT_MS,
        }
      )
      .then((response) => {
        if (cancelled) return;
        const blob = new Blob([response.data as BlobPart], {
          type: "application/pdf",
        });
        blobRef.current = blob;
        downloadFileNameRef.current = getResponseDownloadFilename(
          response.headers?.["content-disposition"],
          "report.pdf"
        );
        const url = URL.createObjectURL(blob);
        revokedUrl = url;
        setBlobUrl(url);
      })
      .catch((err: any) => {
        if (cancelled) return;

        let userMessage = "The report PDF could not be loaded at this time.";

        if (axios.isAxiosError(err)) {
          if (err.response?.status === 404) {
            userMessage =
              "This historical report does not have a cached PDF available.";
          } else if (err.code === "ECONNABORTED" || !err.response) {
            userMessage =
              "Unable to connect to the server. Please check your connection and try again.";
          }
        }

        setError(userMessage);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [opened, caseId, lastSigned]);

  const handleDownload = useCallback(() => {
    if (blobRef.current) {
      triggerBlobDownload(blobRef.current, downloadFileNameRef.current);
    }
  }, []);

  const handlePrint = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) {
      notifications.show({
        color: "red",
        title: "Print failed",
        message: "Report not ready to print yet.",
      });
      return;
    }
    win.focus();
    win.print();
  }, []);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Report${caseNumber ? ` - ${caseNumber}` : ""}`}
      size="90%"
      centered
      styles={{
        body: { padding: 0 },
        content: { display: "flex", flexDirection: "column" },
      }}
    >
      <Stack gap={0} style={{ height: "80vh" }}>
        <Box style={{ flex: 1, minHeight: 0, position: "relative" }}>
          {loading && (
            <Center style={{ height: "100%" }}>
              <Loader size="md" />
            </Center>
          )}
          {error && !loading && (
            <Box p="md">
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Could not load report"
                color="red"
              >
                {error}
              </Alert>
            </Box>
          )}
          {blobUrl && !loading && !error && (
            <iframe
              ref={iframeRef}
              src={`${blobUrl}#toolbar=0&navpanes=0&zoom=FitW`}
              title="Signed report PDF"
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          )}
        </Box>
        <Group
          justify="space-between"
          gap="xs"
          p="sm"
          style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
        >
          <Button variant="subtle" onClick={onClose}>
            Close
          </Button>
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              onClick={handlePrint}
              disabled={!blobUrl}
            >
              Print
            </Button>
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={handleDownload}
              disabled={!blobRef.current}
            >
              Download
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
